import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Tooltip,
  Link,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ArticleIcon from '@mui/icons-material/Article';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useSiteStore } from '../../store/site.store';
import { usePillarDetail } from '../../hooks/usePillarDetail';
import { activationService } from '../../services/activationService';

const PILLAR_ID = 'ai_searchability';

// ── helpers ──────────────────────────────────────────────────────────────────

const getNum = (raw: Record<string, unknown> | undefined, key: string): number => {
  if (!raw) return 0;
  const v = raw[key];
  if (v === null || v === undefined) return 0;
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

const getBool = (raw: Record<string, unknown> | undefined, key: string, fallback = false): boolean => {
  if (!raw) return fallback;
  const v = raw[key];
  if (v === null || v === undefined) return fallback;
  return Boolean(v);
};

/** 'allowed' | 'blocked' | 'unknown' */
const botStatus = (raw: Record<string, unknown> | undefined, key: string): 'allowed' | 'blocked' | 'unknown' => {
  if (!raw) return 'unknown';
  const v = raw[key];
  if (v === 'ALLOWED') return 'allowed';
  if (v === 'BLOCKED') return 'blocked';
  return 'unknown';
};

const pct = (part: number, total: number): number =>
  total > 0 ? Math.round((part / total) * 100) : 0;

// ── sub-components ────────────────────────────────────────────────────────────

const statusColor = (s: 'PASS' | 'FAIL' | 'INFO') =>
  s === 'PASS' ? 'success.main' : s === 'FAIL' ? 'error.main' : 'info.main';

const StatusIcon: React.FC<{ status: 'PASS' | 'FAIL' | 'INFO' }> = ({ status }) => {
  if (status === 'PASS') return <CheckCircleIcon color="success" />;
  if (status === 'FAIL') return <ErrorIcon color="error" />;
  return <InfoIcon color="info" />;
};

interface SignalCardProps {
  title: string;
  icon: React.ReactNode;
  status: 'PASS' | 'FAIL' | 'INFO';
  headline: string;
  details: { label: string; value: string | number }[];
  progress?: number; // 0–100, shown as bar when provided
}

const SignalCard: React.FC<SignalCardProps> = ({ title, icon, status, headline, details, progress }) => (
  <Card variant="outlined" sx={{ height: '100%', borderTop: 3, borderTopColor: statusColor(status) }}>
    <CardContent>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Box color={statusColor(status)}>{icon}</Box>
        <Typography variant="subtitle1" fontWeight="bold">
          {title}
        </Typography>
        <Box ml="auto">
          <Tooltip title={status}>
            <span>
              <StatusIcon status={status} />
            </span>
          </Tooltip>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 36 }}>
        {headline}
      </Typography>
      {progress !== undefined && (
        <LinearProgress
          variant="determinate"
          value={Math.min(progress, 100)}
          color={progress >= 75 ? 'success' : progress >= 50 ? 'warning' : 'error'}
          sx={{ borderRadius: 1, height: 6, mb: 1.5 }}
        />
      )}
      <Divider sx={{ mb: 1 }} />
      {details.map((d) => (
        <Box key={d.label} display="flex" justifyContent="space-between" mb={0.5}>
          <Typography variant="caption" color="text.secondary">
            {d.label}
          </Typography>
          <Typography variant="caption" fontWeight="bold">
            {d.value}
          </Typography>
        </Box>
      ))}
    </CardContent>
  </Card>
);

// ── page ──────────────────────────────────────────────────────────────────────

const AiSearchabilityPillarPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedSite } = useSiteStore();
  const siteId = selectedSite?.id || 0;

  const { data: pillar, isLoading, error } = usePillarDetail(siteId, PILLAR_ID);

  useEffect(() => {
    if (selectedSite?.id && pillar) {
      const sessionId = sessionStorage.getItem('sessionId') || `session_${Date.now()}`;
      activationService
        .trackEvent(selectedSite.id, {
          eventType: 'READINESS_PILLAR_VIEWED',
          sessionId,
          metadata: {
            pillarId: PILLAR_ID,
            pillarName: pillar.pillarName,
            score: pillar.score,
            viewType: 'detail',
          },
        })
        .catch(() => {});
    }
  }, [selectedSite?.id, pillar]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !pillar) {
    return <Alert severity="warning">Could not load AI Searchability data for this site.</Alert>;
  }

  const raw = pillar.rawMetrics as Record<string, unknown> | undefined;

  // ── AI crawler access ──────────────────────────────────────────────────────
  const gptbot     = botStatus(raw, 'gptbot_allowed');
  const claudebot  = botStatus(raw, 'claudebot_allowed');
  const perplexity = botStatus(raw, 'perplexitybot_allowed');
  const allowedCount = [gptbot, claudebot, perplexity].filter((s) => s === 'allowed').length;
  const crawlerStatus: 'PASS' | 'FAIL' | 'INFO' =
    allowedCount === 3 ? 'PASS' : allowedCount === 0 ? 'FAIL' : 'INFO';

  // ── LLM discovery ──────────────────────────────────────────────────────────
  const llmsPresent    = getBool(raw, 'llms_txt_present');
  const llmsValid      = getBool(raw, 'llms_txt_valid');
  const llmsFullPresent = getBool(raw, 'llms_full_txt_present');
  const llmsStatus: 'PASS' | 'FAIL' | 'INFO' =
    llmsPresent && llmsValid ? 'PASS' : llmsPresent ? 'INFO' : 'FAIL';

  // ── Semantic structure (URL-level aggregates) ──────────────────────────────
  const totalUrls    = getNum(raw, 'total_sitemap_urls');
  const semanticUrls = getNum(raw, 'urls_semantic_container');
  const readableUrls = getNum(raw, 'urls_readable_text');
  const noCsrUrls    = getNum(raw, 'urls_no_csr');
  const semanticPct  = pct(semanticUrls, totalUrls);
  const semanticStatus: 'PASS' | 'FAIL' | 'INFO' =
    totalUrls === 0
      ? 'INFO'
      : semanticPct >= 75 && pct(readableUrls, totalUrls) >= 75
      ? 'PASS'
      : semanticPct >= 50
      ? 'INFO'
      : 'FAIL';

  // ── Entity & trust ─────────────────────────────────────────────────────────
  const entityUrls    = getNum(raw, 'urls_entity_schema');
  const authorUrls    = getNum(raw, 'urls_author_signal');
  const trustUrls     = getNum(raw, 'urls_trust_links');
  const citationUrls  = getNum(raw, 'urls_citation_ready');
  const entityPct     = pct(entityUrls, totalUrls);
  const citationPct   = pct(citationUrls, totalUrls);
  const entityStatus: 'PASS' | 'FAIL' | 'INFO' =
    totalUrls === 0
      ? 'INFO'
      : entityPct >= 50 && pct(authorUrls, totalUrls) >= 50
      ? 'PASS'
      : entityPct >= 25
      ? 'INFO'
      : 'FAIL';

  const issues: string[]          = Array.isArray(pillar.issues) ? pillar.issues : [];
  const recommendations: string[] = Array.isArray(pillar.recommendations) ? pillar.recommendations : [];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box mb={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/early-stage')} sx={{ mb: 2 }}>
          Back to Dashboard
        </Button>
        <Box display="flex" alignItems="center" gap={2} mb={1} flexWrap="wrap">
          <Typography variant="h4" component="h1">
            {pillar.pillarName ?? 'AI Searchability'}
          </Typography>
          <Chip
            label={pillar.status}
            color={
              pillar.status === 'READY' ? 'success' : pillar.status === 'PARTIAL' ? 'warning' : 'error'
            }
          />
        </Box>
        <Typography variant="body1" color="text.secondary">
          {pillar.summary}
        </Typography>
      </Box>

      {/* Score Card */}
      <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'white' }}>
        <CardContent>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} md={8}>
              <Typography variant="h6" gutterBottom>
                Overall Score
              </Typography>
              <Typography variant="body2">
                AI search engines (ChatGPT, Perplexity, Claude) use different discovery signals than
                traditional crawlers. These checks confirm your site is accessible and citation-ready
                for LLM-based search.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} textAlign="center">
              <Typography variant="h2" fontWeight="bold" color="white">
                {pillar.score ?? '—'}
              </Typography>
              <Typography variant="body2">/ 100</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Signal Cards */}
      <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
        Site-Level Signals
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* AI Crawler Access */}
        <Grid item xs={12} sm={6} md={3}>
          <SignalCard
            title="AI Crawler Access"
            icon={<SmartToyIcon />}
            status={crawlerStatus}
            headline={
              allowedCount === 3
                ? 'All major AI crawlers allowed'
                : allowedCount === 0
                ? 'All AI crawlers blocked'
                : `${allowedCount}/3 AI crawlers allowed`
            }
            details={[
              { label: 'GPTBot (OpenAI)', value: gptbot === 'allowed' ? '✓ Allowed' : gptbot === 'blocked' ? '✗ Blocked' : '— Unknown' },
              { label: 'ClaudeBot (Anthropic)', value: claudebot === 'allowed' ? '✓ Allowed' : claudebot === 'blocked' ? '✗ Blocked' : '— Unknown' },
              { label: 'PerplexityBot', value: perplexity === 'allowed' ? '✓ Allowed' : perplexity === 'blocked' ? '✗ Blocked' : '— Unknown' },
            ]}
          />
        </Grid>

        {/* LLM Discovery */}
        <Grid item xs={12} sm={6} md={3}>
          <SignalCard
            title="LLM Discovery Files"
            icon={<ArticleIcon />}
            status={llmsStatus}
            headline={
              !llmsPresent
                ? 'llms.txt not found'
                : !llmsValid
                ? 'llms.txt present but invalid'
                : 'llms.txt present and valid'
            }
            details={[
              { label: 'llms.txt present', value: llmsPresent ? 'Yes' : 'No' },
              { label: 'llms.txt valid', value: llmsValid ? 'Yes' : 'No' },
              { label: 'llms-full.txt present', value: llmsFullPresent ? 'Yes' : 'No' },
            ]}
          />
        </Grid>

        {/* Semantic Structure */}
        <Grid item xs={12} sm={6} md={3}>
          <SignalCard
            title="Semantic Structure"
            icon={<AccountTreeIcon />}
            status={semanticStatus}
            headline={
              totalUrls === 0
                ? 'No sitemap URLs tracked'
                : `${semanticPct}% of pages have semantic markup`
            }
            progress={totalUrls > 0 ? semanticPct : undefined}
            details={[
              { label: 'Sitemap URLs', value: totalUrls },
              { label: 'Semantic containers', value: `${semanticUrls} (${pct(semanticUrls, totalUrls)}%)` },
              { label: 'Readable text ≥ 300 chars', value: `${readableUrls} (${pct(readableUrls, totalUrls)}%)` },
              { label: 'No client-side rendering', value: `${noCsrUrls} (${pct(noCsrUrls, totalUrls)}%)` },
            ]}
          />
        </Grid>

        {/* Entity & Trust */}
        <Grid item xs={12} sm={6} md={3}>
          <SignalCard
            title="Entity & Trust Signals"
            icon={<VerifiedIcon />}
            status={entityStatus}
            headline={
              totalUrls === 0
                ? 'No sitemap URLs tracked'
                : entityPct >= 50
                ? `${entityPct}% of pages have entity schema`
                : `Low entity coverage — ${entityPct}%`
            }
            progress={totalUrls > 0 ? citationPct : undefined}
            details={[
              { label: 'Entity schema', value: `${entityUrls} (${entityPct}%)` },
              { label: 'Author signals', value: `${authorUrls} (${pct(authorUrls, totalUrls)}%)` },
              { label: 'Trust links', value: `${trustUrls} (${pct(trustUrls, totalUrls)}%)` },
              { label: 'Citation-ready', value: `${citationUrls} (${citationPct}%)` },
            ]}
          />
        </Grid>
      </Grid>

      {/* Issues */}
      {issues.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              color="error.main"
              display="flex"
              alignItems="center"
              gap={1}
            >
              <WarningAmberIcon /> Issues Found
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <List dense>
              {issues.map((issue, i) => (
                <ListItem key={i} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <ErrorIcon color="error" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={issue} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              color="success.main"
              display="flex"
              alignItems="center"
              gap={1}
            >
              <CheckCircleIcon /> Recommendations
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <List dense>
              {recommendations.map((rec, i) => (
                <ListItem key={i} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleIcon color="success" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={rec} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Educational Resources */}
      <Card sx={{ bgcolor: 'info.light' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
            <InfoIcon /> Learn More
          </Typography>
          <Typography variant="body2" paragraph>
            Resources on AI search optimisation:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2}>
            {[
              { title: 'How to create a llms.txt file', url: 'https://llmstxt.org/' },
              { title: 'Google: Controlling crawling with robots.txt', url: 'https://developers.google.com/search/docs/crawling-indexing/robots/intro' },
              { title: 'Schema.org: Organization entity', url: 'https://schema.org/Organization' },
              { title: 'OpenAI: GPTBot documentation', url: 'https://platform.openai.com/docs/gptbot' },
              { title: 'Perplexity: How AI citation works', url: 'https://www.perplexity.ai/hub/faq/how-does-perplexity-work' },
            ].map((link) => (
              <Link
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  textDecoration: 'none',
                  color: 'primary.main',
                  fontWeight: 500,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {link.title}
                <OpenInNewIcon fontSize="small" />
              </Link>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AiSearchabilityPillarPage;
